import React, { useEffect } from "react";
import CcButton from "./CcButton";
import "./unsaved-changes-dialog.css";

export interface UnsavedChangesDialogProps {
  open: boolean;
  tabLabel: string;
  saving?: boolean;
  onCancel: () => void;
  onDiscard: () => void;
  onSave: () => void;
}

const UnsavedChangesDialog = ({
  open,
  tabLabel,
  saving = false,
  onCancel,
  onDiscard,
  onSave,
}: UnsavedChangesDialogProps) => {
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !saving) onCancel();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onCancel, saving]);

  if (!open) return null;

  return (
    <div
      className="dialog-scrim"
      role="presentation"
      onClick={(event) => {
        if (saving) return;
        if (event.target === event.currentTarget) onCancel();
      }}
    >
      <div
        className="dialog-card unsaved-changes-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="unsaved-changes-dialog-title"
      >
        <div className="dialog-header unsaved-changes-dialog__header">
          <h2 className="dialog-title" id="unsaved-changes-dialog-title">
            Unsaved Changes
          </h2>
          <CcButton variant="icon" aria-label="Close dialog" onClick={onCancel} disabled={saving}>
            ✕
          </CcButton>
        </div>
        <div className="dialog-body">
          <p>
            Unsaved changes detected on the {tabLabel} tab. Switching to another tab will discard
            these changes unless you save.
          </p>
          <p className="unsaved-changes-dialog__note">
            <strong>Note:</strong> Discarded changes cannot be recovered after you leave this tab.
          </p>
        </div>
        <div className="dialog-footer unsaved-changes-dialog__footer">
          <CcButton variant="ghost" onClick={onCancel} disabled={saving}>
            Cancel
          </CcButton>
          <CcButton variant="secondary" onClick={onDiscard} disabled={saving}>
            Discard
          </CcButton>
          <CcButton variant="primary" onClick={onSave} loading={saving}>
            {saving ? "Saving…" : "Save"}
          </CcButton>
        </div>
      </div>
    </div>
  );
};

export default UnsavedChangesDialog;
